/**
 * DataProvider — the only boundary between the UI and where data lives.
 *
 *   UI (pages, components) → hooks (TanStack Query) → services → DataProvider → Mock | API
 *
 * `MockDataProvider` keeps fictional demo data in the browser. `ApiDataProvider` calls the
 * backend's REST API. A backend team can also write its own provider (GraphQL, SDK…) that
 * implements this interface; no page or component has to change.
 *
 * Business rules the original app applied on writes, which a backend must also apply, are
 * listed on each method ("Server rule").
 */
import type {
  Backup,
  DeadlineChangeInput,
  ImportSummary,
  Member,
  MemberInput,
  ParsedDay,
  Project,
  ProjectInput,
  ProjectPatch,
  ProjectUpdate,
  ProjectUpdateFilter,
  ProjectUpdateInput,
  Settings,
  WorkLog,
  WorkLogFilter,
  WorkLogInput,
} from '@/types/models';
import type { ManagementAnalytics, ManagementFilter } from '@/domain/analytics/types';

export interface ProviderCapabilities {
  /** JSON backup export / import */
  backup: boolean;
  /** "Restore demo data" (mock only) */
  resetDemo: boolean;
  /** "Delete all data" */
  deleteAll: boolean;
  /** The provider computes Management View analytics itself (server-side aggregation) */
  serverAnalytics: boolean;
}

export interface DataProvider {
  readonly mode: 'mock' | 'api';
  readonly capabilities: ProviderCapabilities;

  /* Projects */
  listProjects(): Promise<Project[]>;
  getProject(id: string): Promise<Project | null>;
  createProject(input: ProjectInput): Promise<Project>;
  updateProject(id: string, patch: ProjectPatch): Promise<Project>;
  /** Server rule: also deletes the project's work logs and Project Logs. */
  deleteProject(id: string): Promise<void>;
  /**
   * Server rule: no deadline yet → sets `planned` and `deadlineBasis`.
   * Otherwise `to` must be after the current deadline; appends `{on: today, from: current, to, reason}`
   * to `extensions`, sets `revised = to` and `delayReason = reason`. Reason: 10+ characters.
   */
  changeDeadline(id: string, input: DeadlineChangeInput): Promise<Project>;

  /* Members */
  listMembers(): Promise<Member[]>;
  getMember(id: string): Promise<Member | null>;
  /** Server rule: member names are unique (case-insensitive). */
  createMember(input: MemberInput): Promise<Member>;
  updateMember(id: string, patch: Partial<MemberInput>): Promise<Member>;
  /** Server rule: also deletes the member's work logs and removes them from projects and from Settings → Management members. */
  deleteMember(id: string): Promise<void>;

  /* Work logs ("Employee Log Work") */
  listWorkLogs(filter?: WorkLogFilter): Promise<WorkLog[]>;
  /** Server rule: logging time on a project adds the member to that project's members. Hours 0–24. */
  createWorkLog(input: WorkLogInput): Promise<WorkLog>;
  updateWorkLog(id: string, patch: Partial<WorkLogInput>): Promise<WorkLog>;
  deleteWorkLog(id: string): Promise<void>;

  /* Project updates ("Project Log Work") */
  listProjectUpdates(filter?: ProjectUpdateFilter): Promise<ProjectUpdate[]>;
  /** Server rule: one per project per date (reject a duplicate with HTTP 409). */
  createProjectUpdate(input: ProjectUpdateInput): Promise<ProjectUpdate>;
  updateProjectUpdate(id: string, patch: Partial<ProjectUpdateInput>): Promise<ProjectUpdate>;
  deleteProjectUpdate(id: string): Promise<void>;

  /* Settings */
  getSettings(): Promise<Settings>;
  updateSettings(patch: Partial<Settings>): Promise<Settings>;

  /* Bulk */
  /**
   * Server rule: for each day, a work log replaces any existing one for the same date, member and project;
   * a Project Log replaces the one for the same date and project; unknown names create members / projects.
   */
  importDailyUpdate(days: ParsedDay[]): Promise<ImportSummary>;
  exportBackup(): Promise<Backup>;
  /** Replaces all data with the backup. */
  importBackup(backup: Backup): Promise<void>;
  resetDemoData?(): Promise<void>;
  deleteAllData?(): Promise<void>;

  /** Optional server-side aggregation; when absent the frontend computes analytics from the lists above. */
  getManagementAnalytics?(filter: ManagementFilter): Promise<ManagementAnalytics>;
}

/** Error thrown by providers; `status` follows HTTP semantics (404 not found, 409 conflict, 422 invalid…). */
export class DataError extends Error {
  constructor(
    message: string,
    public readonly status = 500,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'DataError';
  }
}
